import { useState } from 'react';
import {
  ArrowRight,
  Loader2,
  Plus,
  Settings2,
  Shield,
  List,
  X,
  Trash2,
} from 'lucide-react';
import { Badge } from '../../../../../components/ui/badge';
import { Button } from '../../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Switch } from '../../../../../components/ui/switch';
import type {
  DialogConfig,
  DialogType,
  EdgeStyle,
  IntakeField,
  IntakeFieldType,
  PostAction,
  PreCondition,
  WorkflowChecklistItem,
  WorkflowChecklistItemType,
  WorkflowStatus,
  WorkflowTransition,
} from '@leal-crm/shared';
import { WORKFLOW_CHECKLIST_ITEM_TYPES } from '@leal-crm/shared';
import type { EmailTemplate, PostactionDefinition, PreconditionDefinition } from '../../types';
import {
  AVAILABLE_ROLES,
  CHECKLIST_SOURCES,
  CHECKLIST_TYPE_LABELS,
  DIALOG_TYPE_LABELS,
  EDGE_STYLE_LABELS,
} from '../../constants';

type CustomFieldDef = IntakeField;

const CUSTOM_FIELD_TYPES: Array<{ value: IntakeFieldType; label: string }> = [
  { value: 'text', label: 'Texto' },
  { value: 'textarea', label: 'Texto largo' },
  { value: 'number', label: 'Numero' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Telefono' },
  { value: 'date', label: 'Fecha' },
  { value: 'select', label: 'Seleccion' },
  { value: 'checkbox', label: 'Checkbox' },
];

interface TransitionEditDialogProps {
  transition: WorkflowTransition | null;
  fromStatus?: WorkflowStatus;
  toStatus?: WorkflowStatus;
  preconditionDefinitions: PreconditionDefinition[];
  postactionDefinitions: PostactionDefinition[];
  emailTemplates: EmailTemplate[];
  checklistItems: WorkflowChecklistItem[];
  onCreateChecklistItem: (data: Omit<WorkflowChecklistItem, 'id' | 'workflowId' | 'createdAt' | 'updatedAt'>) => void;
  onUpdateChecklistItem: (itemId: string, updates: Partial<WorkflowChecklistItem>) => void;
  onDeleteChecklistItem: (itemId: string) => void;
  isSavingChecklist: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (updates: Partial<WorkflowTransition>) => void;
  isSaving: boolean;
}

export function TransitionEditDialog({
  transition,
  fromStatus,
  toStatus,
  preconditionDefinitions,
  postactionDefinitions,
  emailTemplates,
  checklistItems,
  onCreateChecklistItem,
  onUpdateChecklistItem,
  onDeleteChecklistItem,
  isSavingChecklist,
  open,
  onOpenChange,
  onSave,
  isSaving,
}: TransitionEditDialogProps) {
  const [activeSection, setActiveSection] = useState<'basic' | 'requirements' | 'notifications' | 'advanced'>('basic');
  const [formData, setFormData] = useState({
    isEnabled: true,
    dialogType: 'confirm' as DialogType,
    edgeStyle: 'default' as EdgeStyle,
    minTimeInStatusMinutes: undefined as number | undefined,
    maxTimeInStatusMinutes: undefined as number | undefined,
    allowedRoles: [] as string[],
    preConditions: [] as PreCondition[],
    postActions: [] as PostAction[],
    dialogConfig: undefined as DialogConfig | undefined,
  });

  const [syncedTransitionId, setSyncedTransitionId] = useState<string | null>(null);
  const [newRequirement, setNewRequirement] = useState({
    itemKey: '',
    itemLabel: '',
    itemType: 'text' as WorkflowChecklistItemType,
    isRequired: true,
    source: 'workflow',
    options: '',
  });
  const [requirementDrafts, setRequirementDrafts] = useState<Record<string, Partial<WorkflowChecklistItem>>>({});

  const normalizeDialogConfig = (dialogType: DialogType | undefined, config: DialogConfig | undefined) => {
    if (!dialogType || dialogType === 'none' || !config) return undefined;
    const { title, showNotes, notesRequired, showPublicNote } = config;
    if (
      typeof title !== 'string' ||
      typeof showNotes !== 'boolean' ||
      typeof notesRequired !== 'boolean' ||
      typeof showPublicNote !== 'boolean'
    ) {
      return undefined;
    }
    return config;
  };

  const normalizeTransitionPayload = (): Partial<WorkflowTransition> => {
    const minTime = Number.isFinite(formData.minTimeInStatusMinutes)
      ? formData.minTimeInStatusMinutes
      : undefined;
    const maxTime = Number.isFinite(formData.maxTimeInStatusMinutes)
      ? formData.maxTimeInStatusMinutes
      : undefined;
    const preConditions = formData.preConditions.filter((pc) => pc.id && pc.definitionCode);
    const postActions = formData.postActions.filter((pa) => {
      if (!pa.id || !pa.definitionCode) return false;
      if (pa.definitionCode !== 'send_email') return true;
      const templateId = (pa.params as Record<string, unknown> | undefined)?.templateId;
      return typeof templateId === 'string' && templateId.trim().length > 0;
    });

    return {
      isEnabled: formData.isEnabled,
      dialogType: formData.dialogType,
      edgeStyle: formData.edgeStyle,
      minTimeInStatusMinutes: minTime,
      maxTimeInStatusMinutes: maxTime,
      allowedRoles: formData.allowedRoles.length > 0 ? formData.allowedRoles : null,
      preConditions,
      postActions,
      dialogConfig: normalizeDialogConfig(formData.dialogType, formData.dialogConfig),
    };
  };

  const getCustomFields = (): CustomFieldDef[] => {
    return (formData.dialogConfig?.customFields as CustomFieldDef[] | undefined) || [];
  };

  const updateCustomFields = (fields: CustomFieldDef[]) => {
    setFormData((f) => ({
      ...f,
      dialogConfig: {
        ...f.dialogConfig,
        title: f.dialogConfig?.title || 'Confirmar transición',
        showNotes: f.dialogConfig?.showNotes ?? false,
        notesRequired: f.dialogConfig?.notesRequired ?? false,
        showPublicNote: f.dialogConfig?.showPublicNote ?? false,
        customFields: fields,
      },
    }));
  };

  if (transition && syncedTransitionId !== transition.id) {
    setFormData({
      isEnabled: transition.isEnabled,
      dialogType: transition.dialogType,
      edgeStyle: transition.edgeStyle,
      minTimeInStatusMinutes: transition.minTimeInStatusMinutes ?? undefined,
      maxTimeInStatusMinutes: transition.maxTimeInStatusMinutes ?? undefined,
      allowedRoles: transition.allowedRoles || [],
      preConditions: transition.preConditions || [],
      postActions: transition.postActions || [],
      dialogConfig: transition.dialogConfig,
    });
    setSyncedTransitionId(transition.id);
  }

  if (!open && syncedTransitionId !== null) {
    setSyncedTransitionId(null);
    setActiveSection('basic');
  }

  const addPrecondition = (defCode: string) => {
    const def = preconditionDefinitions.find((d) => d.code === defCode);
    if (!def) return;

    const defaultParams = (() => {
      switch (defCode) {
        case 'INTAKE_COMPLETE':
          return {
            minPhotos: 0,
            requireSignature: true,
            requireFuel: true,
            requireMileage: true,
            requireCompleted: true,
          };
        case 'BOOKING_IMAGES_MIN_COUNT':
          return { minCount: 1, source: 'booking_images' };
        case 'ESTIMATE_EXISTS':
          return { table: 'estimates' };
        default:
          return {};
      }
    })();

    const newPrecondition: PreCondition = {
      id: `pc-${Date.now()}`,
      definitionCode: defCode,
      params: defaultParams,
      errorMessage: def.errorMessage,
      isEnabled: true,
    };

    setFormData((f) => ({
      ...f,
      preConditions: [...f.preConditions, newPrecondition],
    }));
  };

  const removePrecondition = (id: string) => {
    setFormData((f) => ({
      ...f,
      preConditions: f.preConditions.filter((pc) => pc.id !== id),
    }));
  };

  const updatePreconditionParams = (id: string, updates: Record<string, unknown>) => {
    setFormData((f) => ({
      ...f,
      preConditions: f.preConditions.map((pc) =>
        pc.id === id ? { ...pc, params: { ...pc.params, ...updates } } : pc
      ),
    }));
  };

  const addPostaction = (defCode: string) => {
    const def = postactionDefinitions.find((d) => d.code === defCode);
    if (!def) return;

    const newPostaction: PostAction = {
      id: `pa-${Date.now()}`,
      definitionCode: defCode,
      params: {},
      order: formData.postActions.length,
      isEnabled: true,
    };

    setFormData((f) => ({
      ...f,
      postActions: [...f.postActions, newPostaction],
    }));
  };

  const removePostaction = (id: string) => {
    setFormData((f) => ({
      ...f,
      postActions: f.postActions.filter((pa) => pa.id !== id),
    }));
  };

  const updateRequirementDraft = (itemId: string, updates: Partial<WorkflowChecklistItem>) => {
    setRequirementDrafts((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], ...updates },
    }));
  };

  const clearRequirementDraft = (itemId: string) => {
    setRequirementDrafts((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };

  const sanitizeConfig = (config: Record<string, unknown> | null | undefined) => {
    if (!config || typeof config !== 'object') return undefined;
    const entries = Object.entries(config).filter(([, value]) => value !== undefined);
    if (entries.length === 0) return undefined;
    return Object.fromEntries(entries);
  };

  const sanitizeChecklistUpdates = (updates: Partial<WorkflowChecklistItem>) => {
    if (!updates.config) return updates;
    return {
      ...updates,
      config: sanitizeConfig(updates.config as Record<string, unknown>),
    };
  };

  const buildRequirementConfig = (data: typeof newRequirement) => {
    if (data.itemType === 'photo' || data.itemType === 'signature' || data.itemType === 'file') {
      const config: Record<string, unknown> = {};
      if (data.source && data.source !== 'workflow') {
        config.source = data.source;
      }
      return Object.keys(config).length > 0 ? config : undefined;
    }
    if (data.itemType === 'select' || data.itemType === 'multiselect') {
      const options = data.options
        .split(',')
        .map((option) => option.trim())
        .filter(Boolean);
      return options.length > 0 ? { options } : undefined;
    }
    return undefined;
  };

  if (!transition || !fromStatus || !toStatus) return null;

  const statusRequirements = checklistItems
    .filter((item) => item.statusId === toStatus.id && item.isActive)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const emailDefinition = postactionDefinitions.find((def) => def.code === 'send_email');
  const emailPostaction = formData.postActions.find((pa) => pa.definitionCode === 'send_email');
  const emailTemplateId = (emailPostaction?.params as Record<string, unknown> | undefined)?.templateId as string | undefined;
  const emailEnabled = !!emailPostaction;
  const customPostActions = formData.postActions.filter((pa) => pa.definitionCode !== 'send_email');
  const availablePostactions = postactionDefinitions.filter((def) => def.code !== 'send_email');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Editar transición
            <Badge variant="outline" className="text-xs font-normal">
              {fromStatus.name} → {toStatus.name}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Configura las condiciones, acciones y diálogo de esta transición
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b pb-2">
          {[
            { key: 'basic', label: 'General', icon: Settings2 },
            { key: 'requirements', label: 'Requisitos', icon: List },
            { key: 'notifications', label: 'Notificaciones', icon: ArrowRight },
            { key: 'advanced', label: 'Avanzado', icon: Shield },
          ].map(({ key, label, icon: Icon }) => (
            <Button
              key={key}
              variant={activeSection === key ? 'default' : 'ghost'}
              size="sm"
              className="gap-1.5 text-xs"
              onClick={() => setActiveSection(key as typeof activeSection)}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </Button>
          ))}
        </div>

        <div className="py-4 min-h-[300px]">
          {activeSection === 'basic' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Transición activa</Label>
                  <p className="text-xs text-muted-foreground">Permitir esta transición</p>
                </div>
                <Switch
                  checked={formData.isEnabled}
                  onCheckedChange={(checked) => setFormData((f) => ({ ...f, isEnabled: checked }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Tipo de diálogo</Label>
                <Select
                  value={formData.dialogType}
                  onValueChange={(v) => setFormData((f) => ({ ...f, dialogType: v as DialogType }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(DIALOG_TYPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Estilo visual</Label>
                <Select
                  value={formData.edgeStyle}
                  onValueChange={(v) => setFormData((f) => ({ ...f, edgeStyle: v as EdgeStyle }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EDGE_STYLE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Roles permitidos</Label>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_ROLES.map((role) => (
                    <Button
                      key={role.value}
                      variant={formData.allowedRoles.includes(role.value) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setFormData((f) => ({
                          ...f,
                          allowedRoles: f.allowedRoles.includes(role.value)
                            ? f.allowedRoles.filter((r) => r !== role.value)
                            : [...f.allowedRoles, role.value],
                        }));
                      }}
                    >
                      {role.label}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {formData.allowedRoles.length === 0 ? 'Todos los roles' : `${formData.allowedRoles.length} roles seleccionados`}
                </p>
              </div>

              <div className="space-y-3 border rounded-lg p-3 bg-muted/30">
                <Label className="text-sm">Opciones del diálogo</Label>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Mostrar notas internas</span>
                  <Switch
                    checked={formData.dialogConfig?.showNotes ?? false}
                    onCheckedChange={(checked) =>
                      setFormData((f) => ({
                        ...f,
                        dialogConfig: {
                          ...f.dialogConfig,
                          title: f.dialogConfig?.title || 'Confirmar transición',
                          showNotes: checked,
                          notesRequired: checked ? (f.dialogConfig?.notesRequired ?? false) : false,
                        },
                      }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Notas obligatorias</span>
                  <Switch
                    checked={formData.dialogConfig?.notesRequired ?? false}
                    disabled={!formData.dialogConfig?.showNotes}
                    onCheckedChange={(checked) =>
                      setFormData((f) => ({
                        ...f,
                        dialogConfig: {
                          ...f.dialogConfig,
                          title: f.dialogConfig?.title || 'Confirmar transición',
                          showNotes: f.dialogConfig?.showNotes ?? false,
                          notesRequired: checked,
                        },
                      }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Nota pública</span>
                  <Switch
                    checked={formData.dialogConfig?.showPublicNote ?? false}
                    onCheckedChange={(checked) =>
                      setFormData((f) => ({
                        ...f,
                        dialogConfig: {
                          ...f.dialogConfig,
                          title: f.dialogConfig?.title || 'Confirmar transición',
                          showNotes: f.dialogConfig?.showNotes ?? false,
                          notesRequired: f.dialogConfig?.notesRequired ?? false,
                          showPublicNote: checked,
                        },
                      }))
                    }
                  />
                </div>
              </div>

              <div className="space-y-3 border rounded-lg p-3 bg-muted/30">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Campos requeridos</Label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const fields = getCustomFields();
                      updateCustomFields([
                        ...fields,
                        {
                          name: `campo_${fields.length + 1}`,
                          type: 'text',
                          label: 'Nuevo campo',
                          required: false,
                        },
                      ]);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Agregar campo
                  </Button>
                </div>

                {getCustomFields().length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Sin campos personalizados.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {getCustomFields().map((field, index) => {
                      const optionsText = (field.options || []).map((opt) => opt.value).join(', ');
                      return (
                        <div key={`${field.name}-${index}`} className="rounded-md border p-3 bg-card space-y-2">
                          <div className="grid gap-2 sm:grid-cols-2">
                            <div className="space-y-1">
                              <Label>Etiqueta</Label>
                              <Input
                                value={field.label}
                                onChange={(e) => {
                                  const fields = getCustomFields().slice();
                                  fields[index] = { ...field, label: e.target.value };
                                  updateCustomFields(fields);
                                }}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label>Clave</Label>
                              <Input
                                value={field.name}
                                onChange={(e) => {
                                  const fields = getCustomFields().slice();
                                  fields[index] = { ...field, name: e.target.value };
                                  updateCustomFields(fields);
                                }}
                              />
                            </div>
                          </div>

                          <div className="grid gap-2 sm:grid-cols-2">
                            <div className="space-y-1">
                              <Label>Tipo</Label>
                              <Select
                                value={field.type}
                                onValueChange={(value) => {
                                  const fields = getCustomFields().slice();
                                  fields[index] = { ...field, type: value as IntakeFieldType };
                                  updateCustomFields(fields);
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {CUSTOM_FIELD_TYPES.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label>Placeholder</Label>
                              <Input
                                value={field.placeholder || ''}
                                onChange={(e) => {
                                  const fields = getCustomFields().slice();
                                  fields[index] = { ...field, placeholder: e.target.value || undefined };
                                  updateCustomFields(fields);
                                }}
                              />
                            </div>
                          </div>

                          {field.type === 'select' && (
                            <div className="space-y-1">
                              <Label>Opciones (separadas por coma)</Label>
                              <Input
                                value={optionsText}
                                onChange={(e) => {
                                  const values = e.target.value
                                    .split(',')
                                    .map((opt) => opt.trim())
                                    .filter(Boolean);
                                  const fields = getCustomFields().slice();
                                  fields[index] = {
                                    ...field,
                                    options: values.map((value) => ({ value, label: value })),
                                  };
                                  updateCustomFields(fields);
                                }}
                              />
                            </div>
                          )}

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={field.required}
                                onCheckedChange={(checked) => {
                                  const fields = getCustomFields().slice();
                                  fields[index] = { ...field, required: checked };
                                  updateCustomFields(fields);
                                }}
                              />
                              <span className="text-sm">Requerido</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => {
                                const fields = getCustomFields().slice();
                                fields.splice(index, 1);
                                updateCustomFields(fields);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Eliminar
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === 'notifications' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Notificaciones</Label>
                  <p className="text-xs text-muted-foreground">
                    Enviar comunicación automática al completar esta transición
                  </p>
                </div>
              </div>

              <div className="space-y-3 border rounded-lg p-3 bg-muted/30">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Email automático</Label>
                    <p className="text-xs text-muted-foreground">
                      Se enviará un email al cliente cuando la transición se complete
                    </p>
                  </div>
                  <Switch
                    checked={emailEnabled}
                    onCheckedChange={(checked) =>
                      setFormData((f) => {
                        const existing = f.postActions.find((pa) => pa.definitionCode === 'send_email');
                        if (checked) {
                          if (existing) {
                            return {
                              ...f,
                              postActions: f.postActions.map((pa) =>
                                pa.definitionCode === 'send_email'
                                  ? { ...pa, isEnabled: true }
                                  : pa
                              ),
                            };
                          }
                          if (!emailDefinition) return f;
                          const defaultTemplateId = emailTemplateId ?? emailTemplates[0]?.id;
                          const newAction: PostAction = {
                            id: `pa-${Date.now()}`,
                            definitionCode: 'send_email',
                            params: defaultTemplateId ? { templateId: defaultTemplateId } : {},
                            order: f.postActions.length,
                            isEnabled: true,
                          };
                          return { ...f, postActions: [...f.postActions, newAction] };
                        }
                        if (!existing) return f;
                        return {
                          ...f,
                          postActions: f.postActions.filter((pa) => pa.definitionCode !== 'send_email'),
                        };
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Plantilla</Label>
                  <Select
                    value={emailTemplateId ?? 'none'}
                    onValueChange={(value) => {
                      const nextTemplate = value === 'none' ? undefined : value;
                      setFormData((f) => {
                        const existing = f.postActions.find((pa) => pa.definitionCode === 'send_email');
                        if (existing) {
                          return {
                            ...f,
                            postActions: f.postActions.map((pa) =>
                              pa.definitionCode === 'send_email'
                                ? { ...pa, params: { ...pa.params, templateId: nextTemplate } }
                                : pa
                            ),
                          };
                        }
                        if (!emailDefinition) return f;
                        const newAction: PostAction = {
                          id: `pa-${Date.now()}`,
                          definitionCode: 'send_email',
                          params: nextTemplate ? { templateId: nextTemplate } : {},
                          order: f.postActions.length,
                          isEnabled: true,
                        };
                        return { ...f, postActions: [...f.postActions, newAction] };
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar plantilla..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin plantilla</SelectItem>
                      {emailTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!emailTemplates.length && (
                    <p className="text-xs text-muted-foreground">
                      No hay plantillas disponibles.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'requirements' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Requisitos del estado destino</Label>
                  <p className="text-xs text-muted-foreground">
                    Captura obligatoria antes de mover a {toStatus.name}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border p-4 bg-muted/30 space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input
                      value={newRequirement.itemLabel}
                      onChange={(e) => {
                        const label = e.target.value;
                        setNewRequirement((prev) => ({
                          ...prev,
                          itemLabel: label,
                          itemKey: prev.itemKey || label
                            .toLowerCase()
                            .trim()
                            .replace(/[^a-z0-9]+/g, '_')
                            .replace(/^_|_$/g, ''),
                        }));
                      }}
                      placeholder="Ej: Kilometraje"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Clave</Label>
                    <Input
                      value={newRequirement.itemKey}
                      onChange={(e) => setNewRequirement((prev) => ({ ...prev, itemKey: e.target.value }))}
                      placeholder="mileage_reading"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select
                      value={newRequirement.itemType}
                      onValueChange={(value) =>
                        setNewRequirement((prev) => ({
                          ...prev,
                          itemType: value as WorkflowChecklistItemType,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WORKFLOW_CHECKLIST_ITEM_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {CHECKLIST_TYPE_LABELS[type]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-3 pt-7">
                    <Switch
                      checked={newRequirement.isRequired}
                      onCheckedChange={(checked) =>
                        setNewRequirement((prev) => ({ ...prev, isRequired: checked }))
                      }
                    />
                    <span className="text-sm">Requerido</span>
                  </div>
                </div>

                {(newRequirement.itemType === 'photo' ||
                  newRequirement.itemType === 'signature' ||
                  newRequirement.itemType === 'file') && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Origen</Label>
                      <Select
                        value={newRequirement.source}
                        onValueChange={(value) =>
                          setNewRequirement((prev) => ({ ...prev, source: value }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CHECKLIST_SOURCES.map((source) => (
                            <SelectItem key={source.value} value={source.value}>
                              {source.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {(newRequirement.itemType === 'select' || newRequirement.itemType === 'multiselect') && (
                  <div className="space-y-2">
                    <Label>Opciones (separadas por coma)</Label>
                    <Input
                      value={newRequirement.options}
                      onChange={(e) => setNewRequirement((prev) => ({ ...prev, options: e.target.value }))}
                      placeholder="Opcion 1, Opcion 2"
                    />
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={() => {
                      if (!newRequirement.itemLabel || !newRequirement.itemKey) return;
                      const nextSortOrder = statusRequirements.length > 0
                        ? Math.max(...statusRequirements.map((item) => item.sortOrder)) + 1
                        : 0;
                      onCreateChecklistItem({
                        statusId: toStatus.id,
                        itemKey: newRequirement.itemKey,
                        itemLabel: newRequirement.itemLabel,
                        itemType: newRequirement.itemType,
                        isRequired: newRequirement.isRequired,
                        config: buildRequirementConfig(newRequirement),
                        sortOrder: nextSortOrder,
                        isActive: true,
                      });
                      setNewRequirement({
                        itemKey: '',
                        itemLabel: '',
                        itemType: 'text',
                        isRequired: true,
                        source: 'workflow',
                        options: '',
                      });
                    }}
                    disabled={
                      isSavingChecklist ||
                      !newRequirement.itemLabel.trim() ||
                      !newRequirement.itemKey.trim()
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Agregar requisito
                  </Button>
                </div>
              </div>

              {statusRequirements.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                  <List className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No hay requisitos configurados</p>
                  <p className="text-xs">La transición no exigirá capturas adicionales</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {statusRequirements.map((item) => {
                    const draft = { ...item, ...requirementDrafts[item.id] };
                    const config = (draft.config || {}) as Record<string, unknown>;
                    const hasDraft = requirementDrafts[item.id] && Object.keys(requirementDrafts[item.id]).length > 0;
                    const source = (config.source as string) || 'workflow';
                    const options = Array.isArray(config.options) ? config.options.join(', ') : '';

                    return (
                      <div key={item.id} className="rounded-lg border p-3 bg-card space-y-3">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label>Nombre</Label>
                            <Input
                              value={draft.itemLabel}
                              onChange={(e) => updateRequirementDraft(item.id, { itemLabel: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Clave</Label>
                            <Input
                              value={draft.itemKey}
                              onChange={(e) => updateRequirementDraft(item.id, { itemKey: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Tipo</Label>
                            <Select
                              value={draft.itemType}
                              onValueChange={(value) =>
                                updateRequirementDraft(item.id, { itemType: value as WorkflowChecklistItemType })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {WORKFLOW_CHECKLIST_ITEM_TYPES.map((type) => (
                                  <SelectItem key={type} value={type}>
                                    {CHECKLIST_TYPE_LABELS[type]}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-3 pt-7">
                            <Switch
                              checked={draft.isRequired}
                              onCheckedChange={(checked) => updateRequirementDraft(item.id, { isRequired: checked })}
                            />
                            <span className="text-sm">Requerido</span>
                          </div>
                        </div>

                        {(draft.itemType === 'photo' || draft.itemType === 'signature' || draft.itemType === 'file') && (
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div className="space-y-2">
                              <Label>Origen</Label>
                              <Select
                                value={source}
                                onValueChange={(value) =>
                                  updateRequirementDraft(item.id, {
                                    config: {
                                      ...config,
                                      source: value === 'workflow' ? undefined : value,
                                    },
                                  })
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {CHECKLIST_SOURCES.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}

                        {(draft.itemType === 'select' || draft.itemType === 'multiselect') && (
                          <div className="space-y-2">
                            <Label>Opciones</Label>
                            <Input
                              value={options}
                              onChange={(e) =>
                                updateRequirementDraft(item.id, {
                                  config: {
                                    ...config,
                                    options: e.target.value
                                      .split(',')
                                      .map((opt) => opt.trim())
                                      .filter(Boolean),
                                  },
                                })
                              }
                            />
                          </div>
                        )}

                        <div className="flex justify-between gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeleteChecklistItem(item.id)}
                            className="text-destructive hover:text-destructive"
                            disabled={isSavingChecklist}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Eliminar
                          </Button>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => clearRequirementDraft(item.id)}
                              disabled={!hasDraft || isSavingChecklist}
                            >
                              Descartar
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                const updates = requirementDrafts[item.id];
                                if (!updates) return;
                                onUpdateChecklistItem(item.id, sanitizeChecklistUpdates(updates));
                                clearRequirementDraft(item.id);
                              }}
                              disabled={!hasDraft || isSavingChecklist}
                            >
                              Guardar
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeSection === 'advanced' && (
            <div className="space-y-5">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Pre-condiciones</Label>
                    <p className="text-xs text-muted-foreground">
                      Reglas avanzadas antes de permitir la transición
                    </p>
                  </div>
                  <Select onValueChange={addPrecondition}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Añadir condición" />
                    </SelectTrigger>
                    <SelectContent>
                      {preconditionDefinitions.map((def) => (
                        <SelectItem key={def.code} value={def.code}>
                          {def.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.preConditions.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-lg">
                    <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No hay pre-condiciones configuradas</p>
                    <p className="text-xs">La transición estará disponible siempre</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.preConditions.map((pc) => {
                      const def = preconditionDefinitions.find((d) => d.code === pc.definitionCode);
                      return (
                        <div
                          key={pc.id}
                          className="space-y-3 p-3 border rounded-lg bg-card"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Switch
                                checked={pc.isEnabled}
                                onCheckedChange={(checked) =>
                                  setFormData((f) => ({
                                    ...f,
                                    preConditions: f.preConditions.map((p) =>
                                      p.id === pc.id ? { ...p, isEnabled: checked } : p
                                    ),
                                  }))
                                }
                              />
                              <div>
                                <p className="text-sm font-medium">{def?.name || pc.definitionCode}</p>
                                <p className="text-xs text-muted-foreground">{def?.description}</p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removePrecondition(pc.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>

                          {pc.definitionCode === 'INTAKE_COMPLETE' && (
                            <div className="grid gap-3 sm:grid-cols-3">
                              <div className="space-y-2">
                                <Label>Min. fotos</Label>
                                <Input
                                  type="number"
                                  min={0}
                                  value={Number(pc.params?.minPhotos ?? 0)}
                                  onChange={(e) =>
                                    updatePreconditionParams(pc.id, { minPhotos: Number(e.target.value) })
                                  }
                                />
                              </div>
                              <div className="flex items-center gap-2 pt-6">
                                <Switch
                                  checked={pc.params?.requireMileage !== false}
                                  onCheckedChange={(checked) =>
                                    updatePreconditionParams(pc.id, { requireMileage: checked })
                                  }
                                />
                                <span className="text-sm">Requiere kilometraje</span>
                              </div>
                              <div className="flex items-center gap-2 pt-6">
                                <Switch
                                  checked={pc.params?.requireFuel !== false}
                                  onCheckedChange={(checked) =>
                                    updatePreconditionParams(pc.id, { requireFuel: checked })
                                  }
                                />
                                <span className="text-sm">Requiere combustible</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={pc.params?.requireSignature !== false}
                                  onCheckedChange={(checked) =>
                                    updatePreconditionParams(pc.id, { requireSignature: checked })
                                  }
                                />
                                <span className="text-sm">Requiere firma</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={pc.params?.requireCompleted !== false}
                                  onCheckedChange={(checked) =>
                                    updatePreconditionParams(pc.id, { requireCompleted: checked })
                                  }
                                />
                                <span className="text-sm">Requiere checklist completo</span>
                              </div>
                            </div>
                          )}

                          {pc.definitionCode === 'BOOKING_IMAGES_MIN_COUNT' && (
                            <div className="grid gap-3 sm:grid-cols-3">
                              <div className="space-y-2">
                                <Label>Min. fotos</Label>
                                <Input
                                  type="number"
                                  min={0}
                                  value={Number(pc.params?.minCount ?? 1)}
                                  onChange={(e) =>
                                    updatePreconditionParams(pc.id, { minCount: Number(e.target.value) })
                                  }
                                />
                              </div>
                              <div className="space-y-2 sm:col-span-2">
                                <Label>Origen</Label>
                                <Select
                                  value={String(pc.params?.source ?? 'booking_images')}
                                  onValueChange={(value) =>
                                    updatePreconditionParams(pc.id, { source: value })
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {CHECKLIST_SOURCES.filter((s) => s.value !== 'workflow').map((source) => (
                                      <SelectItem key={source.value} value={source.value}>
                                        {source.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}

                          {pc.definitionCode === 'ESTIMATE_EXISTS' && (
                            <div className="text-xs text-muted-foreground">
                              Requiere presupuesto asociado (tabla: {String(pc.params?.table ?? 'estimates')}).
                            </div>
                          )}

                          {pc.definitionCode === 'CUSTOMER_APPROVAL' && (
                            <div className="text-xs text-muted-foreground">
                              Bloquea la transición hasta que el cliente apruebe el presupuesto.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Post-acciones</Label>
                    <p className="text-xs text-muted-foreground">
                      Automatizaciones extra al completar la transición
                    </p>
                  </div>
                  <Select onValueChange={addPostaction}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Añadir acción" />
                    </SelectTrigger>
                    <SelectContent>
                      {availablePostactions.map((def) => (
                        <SelectItem key={def.code} value={def.code}>
                          {def.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {customPostActions.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-lg">
                    <ArrowRight className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No hay post-acciones configuradas</p>
                    <p className="text-xs">No se ejecutarán acciones adicionales</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {customPostActions.map((pa, index) => {
                      const def = postactionDefinitions.find((d) => d.code === pa.definitionCode);
                      const isNotification = def?.actionType === 'send_notification';
                      const selectedTemplate = (pa.params?.templateId as string) || '';

                      return (
                        <div
                          key={pa.id}
                          className="border rounded-lg bg-card overflow-hidden"
                        >
                          <div className="flex items-center justify-between p-3">
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className="text-xs w-6 justify-center">
                                {index + 1}
                              </Badge>
                              <Switch
                                checked={pa.isEnabled}
                                onCheckedChange={(checked) =>
                                  setFormData((f) => ({
                                    ...f,
                                    postActions: f.postActions.map((p) =>
                                      p.id === pa.id ? { ...p, isEnabled: checked } : p
                                    ),
                                  }))
                                }
                              />
                              <div>
                                <p className="text-sm font-medium">{def?.name || pa.definitionCode}</p>
                                <p className="text-xs text-muted-foreground">{def?.description}</p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removePostaction(pa.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>

                          {isNotification && (
                            <div className="px-3 pb-3 pt-0 border-t bg-muted/30">
                              <Label className="text-xs text-muted-foreground mb-1.5 block mt-2">
                                Plantilla de Email
                              </Label>
                              <Select
                                value={selectedTemplate}
                                onValueChange={(value) =>
                                  setFormData((f) => ({
                                    ...f,
                                    postActions: f.postActions.map((p) =>
                                      p.id === pa.id
                                        ? { ...p, params: { ...p.params, templateId: value } }
                                        : p
                                    ),
                                  }))
                                }
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="Seleccionar plantilla..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {emailTemplates.map((template) => (
                                    <SelectItem key={template.id} value={template.id} className="text-xs">
                                      {template.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {selectedTemplate && (
                                <p className="text-[10px] text-muted-foreground mt-1">
                                  ID: {selectedTemplate}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            onClick={() => onSave(normalizeTransitionPayload())}
            disabled={isSaving}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Guardar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
